import Template, {ImageInput, ImageValue, TextInput} from '../../../models/Template';
import {Scene, SceneElement, SceneTransforms} from '../../../scene/types';
import {UserLayer} from '../../../scene/userLayers';

/** One entry in the Fill Mode checklist. Two sources share the shape:
 *   'input' — an editable template field, ordered by where its bound element
 *             sits on the board (or by the admin's fillOrder).
 *   'layer' — a text/image layer the USER added (see scene/userLayers). It has
 *             no template input at all, so `input` is absent and every consumer
 *             reads `label` instead of `input.placeholder`.
 *  The layer entries always follow the template fields, so the checklist numbers
 *  of the template's own fields never shift when a layer is added or removed. */
export interface FillField {
    key: string;
    type: 'text' | 'image';
    source: 'input' | 'layer';
    /** ABSENT for a layer. */
    input?: TextInput | ImageInput;
    /** Present only for a layer. */
    layer?: UserLayer;
    /** What a human reads: the input's placeholder, or the layer's label. */
    label: string;
}

interface ElementPosition {
    y: number;
    x: number;
}

/** Absolute axis-aligned box of a bound element on the scene board (scene
 *  coordinates) — used to place the canvas marker overlay over each field. */
export interface InputRect {
    x: number;
    y: number;
    width: number;
    height: number;
    /** The element's rotation in degrees (0 when unset). The box above is the
     *  rotated footprint's AABB; consumers that position rotation-dependent
     *  chrome (the reposition overlay hole) need the raw angle too. */
    rotation?: number;
}

/** A rendered-pixel box (marker overlay coordinates). */
export interface Box2D {
    left: number;
    top: number;
    width: number;
    height: number;
}

/** Intersect a marker box with the canvas bounds [0,0,canvasWidth,canvasHeight]
 *  so no border pixel renders outside the board (templates with edge-hugging
 *  elements would otherwise draw marker outlines off-canvas). Fully-outside
 *  boxes collapse to zero width/height (caller drops those). */
export const clampRectToCanvas = (rect: Box2D, canvasWidth: number, canvasHeight: number): Box2D => {
    const left = Math.max(0, Math.min(rect.left, canvasWidth));
    const top = Math.max(0, Math.min(rect.top, canvasHeight));
    const right = Math.min(canvasWidth, Math.max(rect.left + rect.width, 0));
    const bottom = Math.min(canvasHeight, Math.max(rect.top + rect.height, 0));
    return {left, top, width: Math.max(0, right - left), height: Math.max(0, bottom - top)};
};

/** Rect subtraction: `rect` minus `hole`, as up to 4 remainder rects (above /
 *  below / start / end of the overlap). No overlap returns the rect unchanged;
 *  full containment returns []. Selection-mode hit areas use this to stay off
 *  the Transformer-handle clearance around the repositioned element. */
export const subtractRect = (rect: Box2D, hole: Box2D): Box2D[] => {
    const ix1 = Math.max(rect.left, hole.left);
    const iy1 = Math.max(rect.top, hole.top);
    const ix2 = Math.min(rect.left + rect.width, hole.left + hole.width);
    const iy2 = Math.min(rect.top + rect.height, hole.top + hole.height);
    if (ix1 >= ix2 || iy1 >= iy2) return [rect];
    return [
        {left: rect.left, top: rect.top, width: rect.width, height: iy1 - rect.top},
        {left: rect.left, top: iy2, width: rect.width, height: rect.top + rect.height - iy2},
        {left: rect.left, top: iy1, width: ix1 - rect.left, height: iy2 - iy1},
        {left: ix2, top: iy1, width: rect.left + rect.width - ix2, height: iy2 - iy1},
    ].filter((r) => r.width > 0.5 && r.height > 0.5);
};

/** Depth-first walk of the scene, accumulating group offsets so nested elements
 *  (collage cells etc.) sort by their absolute board position. Returns the first
 *  bound element's position per inputKey. */
const collectInputPositions = (
    elements: (SceneElement & { elements?: SceneElement[] })[] | undefined,
    dx: number,
    dy: number,
    out: Map<string, ElementPosition>
) => {
    for (const el of elements ?? []) {
        if (el.inputKey && el.editable && !out.has(el.inputKey)) {
            out.set(el.inputKey, {y: dy + el.y, x: dx + el.x});
        }
        if (el.elements?.length) {
            collectInputPositions(el.elements, dx + el.x, dy + el.y, out);
        }
    }
};

/** Axis-aligned bounding box of a rect rotated about its TOP-LEFT corner
 *  (Konva's rotation origin for these nodes — x/y stay put, the body swings).
 *  Markers and the selection-mode overlay hole must wrap the rotated footprint,
 *  not the unrotated rect: a 45°-rotated element pokes far outside its w×h box
 *  and its Transformer handles would land under the overlay. */
export const rotatedAABB = (rect: InputRect, rotationDeg: number): InputRect => {
    const deg = ((rotationDeg % 360) + 360) % 360;
    if (deg === 0) return rect;
    const rad = (deg * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    // Corners relative to the rotation origin (the rect's top-left).
    const xs = [0, rect.width * cos, -rect.height * sin, rect.width * cos - rect.height * sin];
    const ys = [0, rect.width * sin, rect.height * cos, rect.width * sin + rect.height * cos];
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    return {
        x: rect.x + minX,
        y: rect.y + minY,
        width: Math.max(...xs) - minX,
        height: Math.max(...ys) - minY,
    };
};

/** Depth-first walk capturing the absolute box (position + size) of the first
 *  bound editable element per inputKey — same offset accumulation as
 *  collectInputPositions. Rotated elements report their rotated AABB. */
const collectInputRectsInto = (
    elements: (SceneElement & { elements?: SceneElement[] })[] | undefined,
    dx: number,
    dy: number,
    out: Map<string, InputRect>,
    transforms?: SceneTransforms,
) => {
    for (const el of elements ?? []) {
        if (el.inputKey && el.editable && !out.has(el.inputKey)) {
            // A drag/pinch persists new geometry into the transforms map (keyed
            // by element id, stage-absolute coords) — the marker box must wrap
            // the element where the user MOVED it, not its original spot.
            // Transform x/y are absolute node coords, so they replace the
            // accumulated group offset rather than add to it.
            const t = transforms?.[el.id];
            const rotation = t?.rotation ?? el.rotation ?? 0;
            out.set(el.inputKey, {
                ...rotatedAABB(
                    {
                        x: t?.x ?? dx + el.x,
                        y: t?.y ?? dy + el.y,
                        width: t?.w ?? el.w,
                        height: t?.h ?? el.h,
                    },
                    rotation,
                ),
                rotation,
            });
        }
        if (el.elements?.length) {
            collectInputRectsInto(el.elements, dx + el.x, dy + el.y, out, transforms);
        }
    }
};

/** Absolute board rects (scene coords) keyed by inputKey, for the marker
 *  overlay. First bound editable element wins per key; non-editable and unbound
 *  elements are skipped; a missing scene yields an empty map. User transforms
 *  (drag/pinch results) override the scene geometry. */
export const collectInputRects = (
    scene: Scene | undefined | null,
    transforms?: SceneTransforms,
): Map<string, InputRect> => {
    const out = new Map<string, InputRect>();
    if (scene) collectInputRectsInto(scene.elements as any, 0, 0, out, transforms);
    return out;
};

/** The Fill Mode field list: only `editable` inputs, ordered top-to-bottom /
 *  right-to-left by their position on the scene board (so the checklist reads in
 *  the same order a person reads the design). Inputs without a bound scene
 *  element — and every input on a legacy HTML design — fall back to the classic
 *  panel's ordering: texts first, then by placeholder. */
export const buildFillFields = (
    inputs: Template['inputs'] | undefined,
    scene: Scene | undefined | null,
    /** The admin's chosen fill order (input keys). When present it is
     *  AUTHORITATIVE for the keys it lists; anything it does not mention falls
     *  through to the geometry sort below and follows after. Absent (the common
     *  case) leaves this function behaving exactly as it always has. */
    fillOrder?: string[] | null
): FillField[] => {
    if (!inputs) return [];

    const positions = new Map<string, ElementPosition>();
    if (scene) collectInputPositions(scene.elements as any, 0, 0, positions);

    return Object.keys(inputs)
        .filter((key) => inputs[key].editable)
        .map((key): FillField => ({
            key,
            type: inputs[key].type,
            source: 'input',
            input: inputs[key],
            label: inputs[key].placeholder,
        }))
        .sort((a, b) => {
            if (fillOrder && fillOrder.length > 0) {
                const orderA = fillOrder.indexOf(a.key);
                const orderB = fillOrder.indexOf(b.key);
                if (orderA !== -1 && orderB !== -1) return orderA - orderB;
                // A field the admin placed always precedes one they did not.
                if (orderA !== -1) return -1;
                if (orderB !== -1) return 1;
            }
            const posA = positions.get(a.key);
            const posB = positions.get(b.key);
            if (posA && posB) {
                if (posA.y !== posB.y) return posA.y - posB.y;
                // RTL app: same row reads right-to-left.
                return posB.x - posA.x;
            }
            // Positioned fields come before unpositioned ones.
            if (posA) return -1;
            if (posB) return 1;
            if (a.type !== b.type) return a.type === 'text' ? -1 : 1;
            return a.label < b.label ? -1 : 1;
        });
};

/** The user's own layers as checklist entries, APPENDED after the template
 *  fields. Order follows the layer array (bottom of the stack first) — the
 *  checklist number is a stable handle, not a z-index reading. */
export const buildLayerFields = (
    layers: UserLayer[] | undefined,
    labelFor: (layer: UserLayer) => string,
): FillField[] => (layers ?? []).map((layer) => ({
    key: layer.id,
    type: layer.kind,
    source: 'layer' as const,
    layer,
    label: labelFor(layer),
}));

/** A field counts as "filled" once the user changed it from the imported
 *  default — that's the checklist's checkmark and the progress numerator. */
export const isFieldFilled = (
    field: FillField,
    selection: string | ImageValue | undefined
): boolean => {
    // A LAYER has no imported default to differ from: the user created it, so
    // an image layer is filled by construction (it cannot exist without an
    // asset) and a text layer is filled once it says anything.
    if (field.source === 'layer') {
        if (field.type === 'image') return true;
        return String(selection ?? '').trim().length > 0;
    }
    if (selection === undefined) return false;
    if (field.type === 'text') {
        const def = ((field.input as TextInput).default ?? '').trim();
        return String(selection).trim() !== def;
    }
    const defThumb = (field.input as ImageInput).default?.thumbnail;
    return (selection as ImageValue)?.thumbnail !== defThumb;
};

export const countFilled = (
    fields: FillField[],
    selections: Record<string, string | ImageValue>
): number => fields.filter((f) => isFieldFilled(f, selections[f.key])).length;

/** A repeated row of the design (Scene.rows) with its fields, in checklist order. */
export interface FieldRow {
    key: string;
    fields: FillField[];
}

export interface FieldGroups {
    /** Fields outside any row (and the user's own layers). */
    ungrouped: FillField[];
    /** Rows on the board, each with the fields it still paints. */
    rows: FieldRow[];
    /** Rows the user hid, with ALL their fields: what was filled is kept. */
    hiddenRows: FieldRow[];
}

/** Split the checklist by row. Fields whose layer was switched off (`off`) are
 *  not on the board, so they leave the checklist and its counts. */
export const groupFillFields = (
    fields: FillField[],
    rowOf: ReadonlyMap<string, string>,
    rowKeys: string[],
    hidden: ReadonlySet<string>,
    off: ReadonlySet<string>,
): FieldGroups => {
    const inRow = (key: string) => fields.filter((f) => rowOf.get(f.key) === key);
    return {
        ungrouped: fields.filter((f) => !rowOf.has(f.key) && !off.has(f.key)),
        rows: rowKeys.filter((key) => !hidden.has(key))
            .map((key) => ({key, fields: inRow(key).filter((f) => !off.has(f.key))})),
        hiddenRows: rowKeys.filter((key) => hidden.has(key)).map((key) => ({key, fields: inRow(key)})),
    };
};

/** A row's first three filled text values — enough to tell "משחק 3" from
 *  "משחק 4" without opening either. */
export const rowSummary = (
    fields: FillField[],
    selections: Record<string, string | ImageValue>,
): string => fields
    .filter((f) => f.type === 'text' && isFieldFilled(f, selections[f.key]))
    .slice(0, 3)
    .map((f) => String(selections[f.key]).trim())
    .join(' · ');
